/**
 * Marketplace — public instructor discovery + detail + lead form.
 * Three views packaged in one file for MVP:
 *   - MarketplaceSearch (/marketplace)
 *   - MarketplaceInstructor (/marketplace/instructor/:id)
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, MapPin, ChevronRight, ChevronLeft, Loader2, Check, Star,
  PoundSterling, BadgeCheck, Send, Mail, Share2, Users, Repeat, ShieldCheck,
  Handshake, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ProfessionalStandardsPanel from "../components/instructor/ProfessionalStandardsPanel";
import { Trophy } from "lucide-react";
import SpecialtyChips from "../components/instructor/SpecialtyChips";
import InstructorTrustStack from "../components/instructor/InstructorTrustStack";
import { composeQuizMessage } from "../utils/quizMessage";
import { useSafeBack } from "../hooks/useSafeBack";

const API = process.env.REACT_APP_BACKEND_URL;

/** Transfer Network trust badge — rendered only when the instructor has
 *  actively joined the network (and isn't paused). Warm, secondary, never
 *  salesy. */
function TransferNetworkBadge({ transferNetwork, testid }) {
  if (!transferNetwork || !transferNetwork.joined) return null;
  const copy = transferNetwork.accepts_relocation
    ? "Part of the Guild Transfer Network · Accepts relocation learners"
    : "Part of the Guild Transfer Network";
  return (
    <span
      data-testid={testid || "mkt-transfer-network-badge"}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/25 border border-violet-200 dark:border-violet-800 rounded-full px-2 py-0.5"
      title="This instructor has opted into the Transfer Network so learners can move instructors without losing progress"
    >
      <Handshake className="w-3 h-3" />
      {copy}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PUBLIC SEARCH PAGE
   ═══════════════════════════════════════════════════════════════ */

/** Quiet aggregate trust signal. Hidden when snapshot is null
 *  (backend only returns a snapshot once the sample is meaningful). */
function RatingSignal({ snapshot, size = "sm", testid }) {
  if (!snapshot || typeof snapshot.avg_rating !== "number") return null;
  const { avg_rating, response_count } = snapshot;
  const sizing =
    size === "lg"
      ? "text-xs"
      : "text-[11px]";
  const star = size === "lg" ? "w-3 h-3" : "w-2.5 h-2.5";
  return (
    <span
      data-testid={testid || "mkt-rating-signal"}
      className={`inline-flex items-center gap-1 ${sizing} text-slate-600 dark:text-slate-300`}
      title={`${response_count} learner reviews`}
    >
      <Star className={`${star} text-amber-500 fill-amber-500`} />
      <span className="font-semibold text-slate-700 dark:text-slate-200">
        {avg_rating.toFixed(1)}
      </span>
      <span className="text-slate-400 dark:text-slate-500">
        average learner rating ({response_count})
      </span>
    </span>
  );
}

/** Quiet host trust signals from Group Sessions data.
 *  Two layouts:
 *    - compact="row"  → single-line with · separators (used on search cards)
 *    - compact="grid" → 3-stat grid + Reliable-Host badge (used on detail page)
 *  Hidden when backend returns `null` (below HOST_MIN_COMPLETED threshold). */
function HostStats({ stats, layout = "row", testid }) {
  if (!stats || !stats.sessions_completed) return null;
  const { sessions_completed, avg_attendance_pct, repeat_attendee_pct, reliable_host } = stats;

  if (layout === "grid") {
    const showRepeat = typeof repeat_attendee_pct === "number";
    const cols = showRepeat ? "grid-cols-3" : "grid-cols-2";
    return (
      <div
        data-testid={testid || "mkt-host-stats"}
        className={`grid ${cols} gap-2 mt-3`}
      >
        <div className="bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3 py-2.5 border border-slate-200/70 dark:border-slate-700">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <Users className="w-3 h-3" /> Sessions
          </div>
          <div className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
            {sessions_completed}
          </div>
        </div>
        <div className="bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3 py-2.5 border border-slate-200/70 dark:border-slate-700">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <BadgeCheck className="w-3 h-3" /> Avg attendance
          </div>
          <div className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
            {avg_attendance_pct}%
          </div>
        </div>
        {showRepeat && (
          <div
            data-testid="mkt-host-stats-repeat"
            className="bg-slate-50 dark:bg-slate-900/40 rounded-lg px-3 py-2.5 border border-slate-200/70 dark:border-slate-700"
          >
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <Repeat className="w-3 h-3" /> Repeat
            </div>
            <div className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
              {repeat_attendee_pct}%
            </div>
          </div>
        )}
      </div>
    );
  }

  // Compact single-line for cards
  return (
    <div
      data-testid={testid || "mkt-host-stats"}
      className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-slate-600 dark:text-slate-300"
    >
      <span className="inline-flex items-center gap-1">
        <Users className="w-2.5 h-2.5 text-slate-400" />
        <span className="font-semibold">{sessions_completed}</span>
        <span className="text-slate-400">sessions</span>
      </span>
      <span className="text-slate-300 dark:text-slate-600">·</span>
      <span className="inline-flex items-center gap-1">
        <span className="font-semibold">{avg_attendance_pct}%</span>
        <span className="text-slate-400">attendance</span>
      </span>
      {reliable_host && (
        <>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span
            data-testid="mkt-host-reliable-inline"
            className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
          >
            <ShieldCheck className="w-3 h-3" />
            <span className="font-semibold">Reliable host</span>
          </span>
        </>
      )}
    </div>
  );
}


export default function MarketplaceSearch() {
  const nav = useNavigate();
  const location = useLocation();
  const goBack = useSafeBack();
  // Restore filter state when returning from an instructor profile
  // (handed back via nav-state) or honour ?postcode= / ?specialty= URL params.
  const incoming = location.state || {};
  const urlParams = new URLSearchParams(location.search);
  const [postcode, setPostcode] = useState(incoming.postcode || urlParams.get("postcode") || "");
  const [specialty, setSpecialty] = useState(incoming.specialty || urlParams.get("specialty") || "");
  const [specialties, setSpecialties] = useState([]);
  const [specialtyGroups, setSpecialtyGroups] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/marketplace/specialties`).then(r => {
      setSpecialties(r.data.specialties || []);
      setSpecialtyGroups(r.data.groups || []);
    });
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/marketplace/search`, {
        params: { postcode, specialty, limit: 50 },
      });
      setData(d);
    } catch {
      toast.error("Search failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-2xl mx-auto px-4 py-10">
          <button data-testid="mkt-back" onClick={() => goBack()} className="mb-3 opacity-80 hover:opacity-100">
            <ChevronLeft className="w-5 h-5 inline" /> Back
          </button>
          <h1 className="text-3xl font-extrabold mb-1">Find your driving instructor</h1>
          <p className="text-sm text-white/80 flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4" /> Verified platform members only. Search by postcode to find instructors near you.
          </p>

          {/* Find My Best Match — guided wizard entry */}
          <button
            data-testid="mkt-match-wizard-cta"
            onClick={() => nav("/marketplace/match")}
            className="mt-4 w-full group relative overflow-hidden rounded-2xl border border-amber-300/60 bg-gradient-to-r from-amber-400/95 to-amber-500/95 hover:from-amber-300 hover:to-amber-400 text-slate-950 px-4 py-3.5 text-left transition active:scale-[0.99]"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-slate-950 text-amber-400 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-wider opacity-80">Not sure who's right for you?</div>
                <div className="text-sm font-extrabold">Find My Best Match — 60-second quiz</div>
              </div>
              <ChevronRight className="w-5 h-5 opacity-70 group-hover:translate-x-0.5 transition" />
            </div>
          </button>

          <div className="mt-5 space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="mkt-postcode"
                  value={postcode}
                  onChange={e => setPostcode(e.target.value.toUpperCase())}
                  placeholder="e.g. SW1A 1AA"
                  className="pl-10 h-12 bg-white text-slate-900"
                  onKeyDown={e => e.key === "Enter" && search()}
                />
              </div>
              <Button data-testid="mkt-search" onClick={search} disabled={loading}
                className="h-12 px-5 bg-white text-blue-700 hover:bg-slate-100 font-bold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" /> Search</>}
              </Button>
            </div>
            <select
              data-testid="mkt-specialty"
              value={specialty}
              onChange={e => { setSpecialty(e.target.value); setTimeout(search, 0); }}
              className="w-full h-10 px-3 rounded-lg bg-white/10 text-white text-sm border border-white/20">
              <option value="" className="text-slate-900">All specialisms</option>
              {specialtyGroups.length > 0 ? (
                specialtyGroups.map(g => (
                  <optgroup key={g.key} label={g.label}>
                    {g.specialties.map(s => <option key={s} value={s} className="text-slate-900">{s}</option>)}
                  </optgroup>
                ))
              ) : (
                specialties.map(s => <option key={s} value={s} className="text-slate-900">{s}</option>)
              )}
            </select>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {data && (
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3" data-testid="mkt-match-level">
            {data.total === 0 ? "No instructors match yet — check back soon." :
              data.match_level === "exact_outward" ? `${data.total} in your area (${postcode})` :
              data.match_level === "same_area" ? `${data.total} nearby` :
              `${data.total} instructor${data.total === 1 ? "" : "s"}`}
          </p>
        )}

        <div className="grid grid-cols-1 gap-3">
          {(data?.results || []).map(i => (
            <InstructorCard key={i.user_id} i={i} onOpen={() => nav(`/marketplace/instructor/${i.user_id}`, { state: { postcode, specialty } })} />
          ))}
          {data && data.results?.length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center">
              <p className="text-sm font-bold text-slate-900 dark:text-white mb-1">No instructors yet</p>
              <p className="text-xs text-slate-500">Try a different postcode or specialty.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstructorCard({ i, onOpen }) {
  const initial = (i.name || i.business_name || "I").trim().charAt(0).toUpperCase();
  return (
    <button
      data-testid={`mkt-instructor-${i.user_id}`}
      onClick={onOpen}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-left hover:border-blue-300 active:scale-[0.98] transition flex gap-3 items-center shadow-sm">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-2xl font-extrabold flex-shrink-0">
        {i.photo_url ? (
          <img src={i.photo_url} alt={i.name} className="w-full h-full rounded-2xl object-cover" />
        ) : initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-bold text-slate-900 dark:text-white text-sm truncate">
            {i.business_name || i.name}
          </p>
          {i.verified && <BadgeCheck className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" title="Verified platform member" />}
          {i.is_trainee && (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Trainee</span>
          )}
          {i.accepting_new_pupils && <span className="text-[9px] font-semibold text-emerald-600">· Available</span>}
          <StandardsChip standards={i.standards} testid={`mkt-standards-${i.user_id}`} />
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
          {i.postcode_outward ? `${i.postcode_outward} · ` : ""}{i.area || ""}
          {i.hourly_rate && <> · £{i.hourly_rate}/hr</>}
        </p>
        {i.rating_snapshot && (
          <div className="mt-1">
            <RatingSignal snapshot={i.rating_snapshot} testid={`mkt-rating-${i.user_id}`} />
          </div>
        )}
        {i.host_stats && (
          <HostStats stats={i.host_stats} layout="row" testid={`mkt-host-stats-${i.user_id}`} />
        )}
        <div className="mt-1.5">
          <InstructorTrustStack instructorId={i.user_id} max={4} />
        </div>
        {i.specialties && i.specialties.length > 0 && (
          <div className="mt-1.5">
            <SpecialtyChips items={i.specialties} max={3} testidPrefix={`mkt-spec-${i.user_id}`} />
          </div>
        )}
        {i.transfer_network?.joined && (
          <div className="mt-1.5">
            <TransferNetworkBadge
              transferNetwork={i.transfer_network}
              testid={`mkt-transfer-badge-${i.user_id}`}
            />
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INSTRUCTOR DETAIL + LEAD FORM
   ═══════════════════════════════════════════════════════════════ */
export function MarketplaceInstructor() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const quizState = location.state || {};
  const [instructor, setInstructor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", message: "" });
  const [prefilledFromQuiz, setPrefilledFromQuiz] = useState(false);
  const contactRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/marketplace/instructor/${id}`);
      setInstructor(data);
    } catch {
      toast.error("Instructor not found");
      nav("/marketplace");
    }
    setLoading(false);
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  // Pre-fill message + postcode from Best Match quiz state, once instructor is loaded.
  // Only fires if the user explicitly tapped "Message" on a match card
  // (prefillFromQuiz=true). Form is editable — we never lock the field.
  useEffect(() => {
    if (!instructor || prefilledFromQuiz) return;
    if (!quizState.prefillFromQuiz || !quizState.answers) return;
    const msg = composeQuizMessage({
      instructorName: quizState.instructorName || instructor.name || instructor.business_name,
      ...quizState.answers,
    });
    setForm((f) => ({
      ...f,
      message: f.message || msg,
      postcode: f.postcode || (quizState.answers.postcode || "").toUpperCase(),
    }));
    setPrefilledFromQuiz(true);
    // Scroll into view after a tick so the layout has settled.
    if (quizState.scrollToContact && contactRef.current) {
      setTimeout(() => {
        contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }
  }, [instructor, quizState, prefilledFromQuiz]);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/marketplace/leads`,
        { instructor_user_id: id, ...form }, { withCredentials: true });
      setSent(true);
      toast.success("Inquiry sent — they'll be in touch.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSubmitting(false);
  };

  const share = async () => {
    const shareUrl = `${window.location.origin}/api/marketplace/og/instructor/${id}`;
    const shareData = {
      title: instructor?.business_name || instructor?.name || "Driving instructor",
      text: `Check out ${instructor?.name || "this instructor"} on National Driving Guild`,
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste into WhatsApp, Instagram, FB, etc.");
    }
  };

  if (loading) return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!instructor) return null;

  const initial = (instructor.name || instructor.business_name || "I").trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button
            data-testid="mkt-detail-back"
            onClick={() => nav("/marketplace", {
              state: {
                postcode: quizState.postcode || (quizState.answers && quizState.answers.postcode) || "",
                specialty: quizState.specialty || "",
              },
            })}
            className="mb-4 opacity-80 hover:opacity-100"
          >
            <ChevronLeft className="w-5 h-5 inline" /> Back to search
          </button>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-3xl bg-white/20 backdrop-blur-sm text-white flex items-center justify-center text-3xl font-extrabold flex-shrink-0">
              {instructor.photo_url ? <img src={instructor.photo_url} alt="" className="w-full h-full rounded-3xl object-cover" /> : initial}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-extrabold">{instructor.business_name || instructor.name}</h1>
                {instructor.verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-[10px] font-bold uppercase">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </span>
                )}
                {instructor.is_trainee && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-400/30 text-[10px] font-bold uppercase">Trainee</span>
                )}
                {instructor.host_stats?.reliable_host && (
                  <span
                    data-testid="mkt-detail-reliable-host"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/30 text-[10px] font-bold uppercase"
                    title="3+ sessions completed · ≥50% avg attendance · low cancellation rate"
                  >
                    <ShieldCheck className="w-3 h-3" /> Reliable host
                  </span>
                )}
              </div>
              <p className="text-sm opacity-80">{instructor.area || instructor.postcode_outward || "UK"}</p>
              {instructor.accepting_new_pupils && (
                <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full bg-emerald-400/30 text-[10px] font-bold uppercase"><Check className="w-3 h-3" /> Taking new pupils</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Public Professional Standards trust signals */}
        <ProfessionalStandardsPanel instructorId={id} mode="public" />

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <div className="flex gap-4 mb-3 text-sm items-center flex-wrap">
            {instructor.hourly_rate && (
              <div className="flex items-center gap-1"><PoundSterling className="w-4 h-4 text-emerald-600" /><strong>£{instructor.hourly_rate}</strong><span className="text-slate-500">/hr</span></div>
            )}
            {instructor.rating_snapshot && (
              <RatingSignal
                snapshot={instructor.rating_snapshot}
                size="lg"
                testid="mkt-detail-rating"
              />
            )}
          </div>
          {instructor.host_stats && (
            <HostStats
              stats={instructor.host_stats}
              layout="grid"
              testid="mkt-detail-host-stats"
            />
          )}
          {instructor.transfer_network?.joined && (
            <div className="mt-3">
              <TransferNetworkBadge
                transferNetwork={instructor.transfer_network}
                testid="mkt-detail-transfer-badge"
              />
            </div>
          )}
          {instructor.bio && <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-line">{instructor.bio}</p>}
          {instructor.specialties?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {instructor.specialties.map(s => (
                <span key={s} className="text-[11px] font-semibold px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  <Star className="w-2.5 h-2.5 inline mr-0.5" /> {s}
                </span>
              ))}
            </div>
          )}
          <button data-testid="mkt-share" onClick={share}
            className="mt-4 w-full flex items-center justify-center gap-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold py-2 rounded-lg transition">
            <Share2 className="w-3.5 h-3.5" /> Share this instructor
          </button>
        </div>

        {sent ? (
          <div data-testid="mkt-lead-sent" className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-base font-bold text-emerald-800 dark:text-emerald-200 mb-1">Inquiry sent</h3>
            <p className="text-xs text-emerald-700 dark:text-emerald-300">{instructor.name || "Your instructor"} will be in touch soon.</p>
          </div>
        ) : (
          <div ref={contactRef} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-blue-600" />
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Send an inquiry</h3>
            </div>
            {prefilledFromQuiz && (
              <div
                data-testid="lead-quiz-prefill-banner"
                className="flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl px-3 py-2"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                    Pre-filled from your Best Match quiz
                  </div>
                  <div className="text-[11px] text-amber-700 dark:text-amber-200/80 mt-0.5">
                    Feel free to tweak the wording before sending.
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Your name</Label><Input data-testid="lead-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label><Input data-testid="lead-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs">Phone (optional)</Label><Input data-testid="lead-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label className="text-xs">Your postcode</Label><Input data-testid="lead-postcode" value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value.toUpperCase() })} /></div>
            </div>
            <div><Label className="text-xs">Message</Label>
              <Textarea data-testid="lead-message" rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                placeholder="Hi! I'm looking for lessons starting..." /></div>
            <Button data-testid="lead-submit" onClick={submit} disabled={submitting}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send inquiry</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STANDARDS CHIP — small public trust signal on each search row
   ═══════════════════════════════════════════════════════════════ */
const TIER_CHIP = {
  black:    { label: "Master", cls: "bg-zinc-900 text-amber-300 border-amber-500/40" },
  platinum: { label: "Elite",  cls: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200" },
  gold:     { label: "Pro",    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
};
function StandardsChip({ standards, testid }) {
  if (!standards) return null;
  const tierMeta = TIER_CHIP[standards.tier];
  const matchLabel = labelFromMatch(standards.matched_specialty);
  if (!tierMeta && !standards.is_compliant && !matchLabel) return null;
  // Per spec: max 2-3 chips. Order: Match → Compliant → Tier badge.
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 flex-wrap">
      {matchLabel && (
        <span
          data-testid={`${testid}-match`}
          title={`Matched on "${standards.matched_specialty}" — boosted to the top of your search.`}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-extrabold uppercase tracking-wider shadow-sm shadow-emerald-500/40"
        >
          <Sparkles className="w-2.5 h-2.5" />
          {matchLabel}
        </span>
      )}
      {standards.is_compliant && (
        <span
          data-testid={`${testid}-compliant`}
          title="MOT, insurance, vehicle tax and service all configured and up to date."
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 text-[9px] font-extrabold uppercase tracking-wider"
        >
          <ShieldCheck className="w-2.5 h-2.5" />
          Compliant
        </span>
      )}
      {tierMeta && (
        <span
          data-testid={`${testid}-tier`}
          title={`Operating Standards · ${tierMeta.label}`}
          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider border ${tierMeta.cls}`}
        >
          <Trophy className="w-2.5 h-2.5" />
          {tierMeta.label}
        </span>
      )}
    </span>
  );
}

/**
 * Convert the raw matched_specialty into a tasteful learner-facing label.
 * Falls back to "Matches Your Search" when no friendly mapping exists.
 */
function labelFromMatch(spec) {
  if (!spec) return null;
  const s = spec.toLowerCase();
  // Curated friendly labels — keep short, sharp, max ~22 chars
  if (s.includes("automatic")) return "Automatic Specialist";
  if (s.includes("manual lessons")) return "Manual Specialist";
  if (s.includes("nervous")) return "Nervous Learner Match";
  if (s.includes("anxiety")) return "Anxiety-Aware Match";
  if (s.includes("first-time")) return "Beginner Match";
  if (s.includes("confidence")) return "Confidence Match";
  if (s.includes("refresher")) return "Refresher Match";
  if (s.includes("test prep")) return "Test Prep Match";
  if (s.includes("neurodivergent")) return "Neurodivergent Match";
  if (s.includes("adhd") || s.includes("autism")) return "ADHD/Autism Match";
  if (s.includes("calm step")) return "Calm Coaching Match";
  if (s.includes("motorway")) return "Motorway Specialist";
  if (s.includes("night")) return "Night Driving Match";
  if (s.includes("advanced")) return "Advanced Coaching";
  if (s.includes("defensive")) return "Defensive Driving";
  if (s.includes("ev")) return "EV Specialist";
  if (s.includes("hybrid")) return "Hybrid Specialist";
  if (s.includes("trailer") || s.includes("towing")) return "Trailer Specialist";
  if (s.includes("caravan")) return "Caravan Towing";
  if (s.includes("horsebox")) return "Horsebox Towing";
  if (s.includes("intensive") || s.includes("fast-track")) return "Intensive Course Match";
  if (s.includes("block booking")) return "Block Booking Match";
  // Generic fallback
  return "Matches Your Search";
}
